import {
  Domains200DomainsItem,
  HostingDomainAvailability200,
  HostingDomainsV2200DomainsItem,
  HostingSitesV2200ItemsItem,
  SitesDomainsV2200DomainsItem,
  SitesInfo200,
} from '@newfold/huapi-js/src/index.schemas';

import { getIsDevMode } from '~/components/DevMode';

import { DomainPresenceFlags } from './types';

/*
  Given a domain and a site, determines whether the docroot where the
  domain is installed matches the location where the site is installed
*/
export const checkDomainDocrootEqualsSiteDocpath = (
  domain?: SitesDomainsV2200DomainsItem | HostingDomainsV2200DomainsItem,
  site?: SitesInfo200 | HostingSitesV2200ItemsItem,
) =>
  domain?.docroot ===
  (site?.path ? `${site?.docroot}/${site?.path}` : site?.docroot);

export function handleDomainFlow(data: HostingDomainAvailability200) {
  const {
    registered,
    assignable,
    assigned,
    can_assign,
    assigned_to_this_product,
  } = data;
  // can_assign will be false if user is tech user, for which not permitted to assign domain
  const isUserPermittedToAssign = !can_assign;
  // Domain is not registered / assigned to another account but assignable - Show user that domain is not registered (Future -once CTB to be shown rather than error)
  const isRegistrationNeeded = !registered && assignable && !assigned;
  // if assigned is true that means it is assigned to another account, hence show the error
  const isDomainInAnotherAccount = assigned && !assigned_to_this_product;
  // We are already checking that if  domain is already in same site / another site on the account..this is a fail safe condition for the same
  const isDomainAssignedToSameAccount = assigned_to_this_product;
  // In beta env, domains are not available in DNS servers. Hence for beta we have to bypass if registered === null
  const isBetaByPassCheck =
    getIsDevMode(['development', 'beta']) && registered === null;
  const isBypassSFAPI = isDomainAssignedToSameAccount ?? false;
  // If all above conditions failed, domain is allowed for assignment
  const isDomainAllowedForAssign =
    assignable && (registered || isBetaByPassCheck);

  if (isUserPermittedToAssign) {
    return {
      isError: true,
      code: 'domainAssignPermissionError',
      skipSFAPI: isBypassSFAPI,
    };
  } else if (isDomainInAnotherAccount) {
    return {
      isError: true,
      code: 'existingDomainAddError',
      skipSFAPI: isBypassSFAPI,
    };
  } else if (isRegistrationNeeded && !isBetaByPassCheck) {
    return {
      isError: true,
      code: 'domainNotRegistered',
      skipSFAPI: isBypassSFAPI,
    };
  } else if (isDomainAssignedToSameAccount) {
    return { isError: false, code: '', skipSFAPI: isBypassSFAPI };
  } else if (isDomainAllowedForAssign) {
    return { isError: false, code: '', skipSFAPI: isBypassSFAPI };
  }
  return { isError: true, code: 'unknownReason', skipSFAPI: isBypassSFAPI };
}

/**
 * Checks the presence and usage of a domain in the account's domain list.
 * Returns flags indicating if the domain is present in the same site, another site,
 * or is the main domain in use by a parent site.
 */
export function checkDomainExistsInAccount(
  siteInfo: SitesInfo200,
  domainName: string,
  domainList: Domains200DomainsItem[] = [],
): DomainPresenceFlags {
  let isDomainPresentInSameSite = false;
  let isDomainPresentInAnotherSite = false;
  let isMainDomainInUseByParentSite = false;
  for (const domain of domainList) {
    if (domain.domain !== domainName) continue;
    const isSameDocroot = domain.docroot === siteInfo?.docroot;
    if (isSameDocroot) {
      if (
        domain.type === 'main' &&
        domain.autogen_id === null &&
        siteInfo?.path !== ''
      ) {
        isMainDomainInUseByParentSite = true;
      } else {
        isDomainPresentInSameSite = true;
      }
    } else {
      isDomainPresentInAnotherSite = true;
    }
    // Early exit if all flags are true
    if (
      isDomainPresentInSameSite &&
      isDomainPresentInAnotherSite &&
      isMainDomainInUseByParentSite
    ) {
      break;
    }
  }
  return {
    isDomainPresentInSameSite,
    isDomainPresentInAnotherSite,
    isMainDomainInUseByParentSite,
  };
}
